import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Helpers.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function Helpers() {
  const [helpers, setHelpers] = useState([
    {
      id: 1,
      name: 'Jake Morrison',
      location: 'Norfolk, NE',
      coordinates: { lat: 47.0527, lng: -109.6333 },
      specialties: ['Cattle', 'Sheep'],
      capabilities: ['Livestock Trailer', 'Tractor', 'First Aid Certified', 'Water Tank'],
      rating: 4.8,
      image: 'https://randomuser.me/api/portraits/men/15.jpg',
      homePhone: '(406) 555-0123',
      mobilePhone: '(406) 555-0124',
      email: 'jake.morrison@email.com'
    },
    {
      id: 2,
      name: 'Maria Rodriguez',
      location: 'Plainview, NE',
      coordinates: { lat: 43.0759, lng: -107.2903 },
      specialties: ['Cattle', 'Horses'],
      capabilities: ['Horse Trailer', 'Veterinary Supplies', 'Communication Radio', 'Heavy Machinery'],
      rating: 4.9,
      image: 'https://randomuser.me/api/portraits/women/22.jpg',
      homePhone: '(307) 555-0456',
      mobilePhone: '(307) 555-0457',
      email: 'maria.rodriguez@email.com'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [newHelper, setNewHelper] = useState({
    name: '',
    location: '',
    coordinates: null,
    specialties: [],
    capabilities: [],
    image: '',
    homePhone: '',
    mobilePhone: '',
    email: ''
  });

  const [showFindPopup, setShowFindPopup] = useState(false);
  const [selectedHelpers, setSelectedHelpers] = useState([]);
  const [showNearbyOnly, setShowNearbyOnly] = useState(false);

  const [showRatingModal, setShowRatingModal] = useState(null);
  const [newRating, setNewRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  // Sample local helpers that can be found and added
  const localHelpers = [
    {
      id: 101,
      name: 'Tom Wilson',
      location: 'Omaha, NE',
      coordinates: { lat: 40.8836, lng: -96.6503 }, // ~8 miles from Lincoln
      specialties: ['Cattle', 'Horses'],
      capabilities: ['Horse Trailer', 'Veterinary Supplies', 'First Aid Certified'],
      rating: 4.7,
      image: 'https://randomuser.me/api/portraits/men/25.jpg',
      homePhone: '(402) 555-0789',
      mobilePhone: '(402) 555-0790',
      email: 'tom.wilson@email.com',
      herds: ['Angus Cattle', 'Quarter Horses']
    },
    {
      id: 102,
      name: 'Lisa Chen',
      location: 'Grand Island, NE',
      coordinates: { lat: 40.9264, lng: -98.3420 }, // ~12+ miles from Lincoln
      specialties: ['Sheep', 'Goats'],
      capabilities: ['Livestock Trailer', 'Shearing Equipment', 'Mobile Vet Kit'],
      rating: 4.9,
      image: 'https://randomuser.me/api/portraits/women/35.jpg',
      homePhone: '(308) 555-0234',
      mobilePhone: '(308) 555-0235',
      email: 'lisa.chen@email.com',
      herds: ['Merino Sheep', 'Boer Goats']
    },
    {
      id: 103,
      name: 'Mike Thompson',
      location: 'Kearney, NE',
      coordinates: { lat: 40.7794, lng: -96.6422 }, // ~6 miles from Lincoln
      specialties: ['Cattle', 'Pigs'],
      capabilities: ['Heavy Machinery', 'Livestock Trailer', 'Emergency Response'],
      rating: 4.6,
      image: 'https://randomuser.me/api/portraits/men/42.jpg',
      homePhone: '(308) 555-0567',
      mobilePhone: '(308) 555-0568',
      email: 'mike.thompson@email.com',
      herds: ['Holstein Cattle', 'Yorkshire Pigs']
    }
  ];

  // Function to calculate distance between two coordinates (in miles)
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Add this helper function after the calculateDistance function
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star"></i>);
    }
    
    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt"></i>);
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star"></i>);
    }
    
    return stars;
  };

  // User's location (you could get this from GPS or user profile)
  const userLocation = { lat: 40.8136, lng: -96.7026 }; // Lincoln, NE as example

  // Filter helpers based on distance
  const getFilteredHelpers = () => {
    if (!showNearbyOnly) return localHelpers;
    
    return localHelpers.filter(helper => {
      const distance = calculateDistance(
        userLocation.lat, userLocation.lng,
        helper.coordinates.lat, helper.coordinates.lng
      );
      return distance <= 10;
    });
  };

  const filteredHelpers = getFilteredHelpers();

  const handleHelperSelection = (helperId, isSelected) => {
    if (isSelected) {
      setSelectedHelpers([...selectedHelpers, helperId]);
    } else {
      setSelectedHelpers(selectedHelpers.filter(id => id !== helperId));
    }
  };

  const handleAddSelectedHelpers = () => {
    const helpersToAdd = localHelpers.filter(helper => 
      selectedHelpers.includes(helper.id)
    );
    setHelpers([...helpers, ...helpersToAdd]);
    setSelectedHelpers([]);
    setShowFindPopup(false);
  };

  const handleMapClick = (coordinates) => {
    setNewHelper({
      ...newHelper,
      coordinates,
      location: `${coordinates.lat.toFixed(4)}, ${coordinates.lng.toFixed(4)}`
    });
  };

  const handleAddHelper = (e) => {
    e.preventDefault();
    if (!newHelper.coordinates) {
      alert('Please select a location on the map');
      return;
    }

    const helper = {
      id: Date.now(),
      ...newHelper,
      rating: 0,
      specialties: newHelper.specialties.split(',').map(s => s.trim()),
      capabilities: newHelper.capabilities.split(',').map(s => s.trim())
    };
    setHelpers([...helpers, helper]);
    setNewHelper({ 
      name: '', 
      location: '', 
      coordinates: null,
      specialties: [], 
      capabilities: [],
      image: '', 
      homePhone: '', 
      mobilePhone: '', 
      email: '' 
    });
    setShowAddForm(false);
    setShowMapPicker(false);
  };

  const handleRemoveHelper = (id) => {
    setHelpers(helpers.filter(helper => helper.id !== id));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewHelper({ ...newHelper, image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRateHelper = (helperId, rating) => {
    // Update the helper's rating (in a real app, this would go to your backend)
    setHelpers(helpers.map(helper => {
      if (helper.id === helperId) {
        // Simple average calculation - in reality you'd track all ratings
        const newAvgRating = ((helper.rating * 10) + rating) / 11; // Assuming 10 previous ratings
        return { ...helper, rating: Math.round(newAvgRating * 10) / 10 };
      }
      return helper;
    }));
    
    setShowRatingModal(null);
    setNewRating(0);
    setHoverRating(0);
  };

  const openRatingModal = (helperId) => {
    setShowRatingModal(helperId);
    setNewRating(0);
    setHoverRating(0);
  };

  // Component for handling map clicks
  const MapClickHandler = ({ onLocationSelect }) => {
    useMapEvents({
      click: (e) => {
        const { lat, lng } = e.latlng;
        onLocationSelect({ lat, lng });
      },
    });
    return null;
  };

  const MapPicker = ({ onLocationSelect, selectedLocation }) => (
    <div className="map-picker">
      <h3>Select Helper Location</h3>
      <div className="map-container">
        <MapContainer
          center={selectedLocation ? [selectedLocation.lat, selectedLocation.lng] : [45.5, -109.5]}
          zoom={selectedLocation ? 10 : 6}
          style={{ height: '300px', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapClickHandler onLocationSelect={onLocationSelect} />
          {selectedLocation && (
            <Marker position={[selectedLocation.lat, selectedLocation.lng]} />
          )}
        </MapContainer>
      </div>
      <div className="map-instructions">
        <p>Click on the map above to select a location</p>
        {selectedLocation && (
          <p className="selected-coordinates">
            Selected: {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="helpers-page">
      <div className="container">
        <div className="helpers-header">
          <h1>Manage Helpers</h1>
          <div className="header-buttons">
            <button
              className="button secondary-button"
              onClick={() => setShowFindPopup(true)}
            >
              Find Helpers
            </button>
            <button
              className="button primary-button"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? 'Cancel' : 'Add Helper'}
            </button>
          </div>
        </div>

        {showFindPopup && (
          <div className="popup-overlay">
            <div className="popup-content">
              <div className="popup-header">
                <h2>Find Local Helpers</h2>
                <button 
                  className="close-button"
                  onClick={() => setShowFindPopup(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="filter-options">
                <label className="checkbox-filter">
                  <input
                    type="checkbox"
                    checked={showNearbyOnly}
                    onChange={(e) => setShowNearbyOnly(e.target.checked)}
                  />
                  <span>Show only helpers within 10 miles</span>
                </label>
              </div>
              
              <div className="helpers-list">
                {filteredHelpers.map(helper => (
                  <div key={helper.id} className="helper-item">
                    <input
                      type="checkbox"
                      id={`helper-${helper.id}`}
                      checked={selectedHelpers.includes(helper.id)}
                      onChange={(e) => handleHelperSelection(helper.id, e.target.checked)}
                    />
                    <label htmlFor={`helper-${helper.id}`} className="helper-label">
                      <img src={helper.image} alt={helper.name} />
                      <div className="helper-details">
                        <h4>{helper.name}</h4>
                        <p>{helper.location} • {calculateDistance(
                          userLocation.lat, userLocation.lng,
                          helper.coordinates.lat, helper.coordinates.lng
                        ).toFixed(1)} miles</p>
                        <div className="rating-small">
                          <span className="stars">{renderStars(helper.rating)}</span>
                          <span className="rating-number">({helper.rating})</span>
                        </div>
                        <div className="specialties">
                          {helper.specialties.map(specialty => (
                            <span key={specialty} className="specialty-tag">{specialty}</span>
                          ))}
                        </div>
                        <div className="herds">
                          <small>Manages: {helper.herds.join(', ')}</small>
                        </div>
                        <div className="capabilities">
                          <small><strong>Capabilities:</strong> {helper.capabilities.join(', ')}</small>
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
              <div className="popup-actions">
                <div className="helper-count">
                  Showing {filteredHelpers.length} helper{filteredHelpers.length !== 1 ? 's' : ''}
                  {showNearbyOnly && ' within 10 miles'}
                </div>
                <div className="action-buttons">
                  <button 
                    className="button secondary-button"
                    onClick={() => setShowFindPopup(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="button primary-button"
                    onClick={handleAddSelectedHelpers}
                    disabled={selectedHelpers.length === 0}
                  >
                    Add Selected ({selectedHelpers.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showAddForm && (
          <div className="add-helper-form">
            <h2>Add New Helper</h2>
            <form onSubmit={handleAddHelper}>
              <input
                type="text"
                placeholder="Name"
                value={newHelper.name}
                onChange={(e) => setNewHelper({...newHelper, name: e.target.value})}
                required
              />
              <div className="location-section">
                <div className="location-input-group">
                  <input
                    type="text"
                    placeholder="Location (will be set from map)"
                    value={newHelper.location}
                    onChange={(e) => setNewHelper({...newHelper, location: e.target.value})}
                    required
                  />
                  <button
                    type="button"
                    className="button secondary-button"
                    onClick={() => setShowMapPicker(!showMapPicker)}
                  >
                    {showMapPicker ? 'Hide Map' : 'Pick on Map'}
                  </button>
                </div>

                {showMapPicker && (
                  <MapPicker
                    onLocationSelect={handleMapClick}
                    selectedLocation={newHelper.coordinates}
                  />
                )}
              </div>
              <input
                type="text"
                placeholder="Specialties (comma separated)"
                value={newHelper.specialties}
                onChange={(e) => setNewHelper({...newHelper, specialties: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="Capabilities (comma separated, e.g., Livestock Trailer, Tractor, First Aid Certified)"
                value={newHelper.capabilities}
                onChange={(e) => setNewHelper({...newHelper, capabilities: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder="Home Phone"
                value={newHelper.homePhone}
                onChange={(e) => setNewHelper({...newHelper, homePhone: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder="Mobile Phone"
                value={newHelper.mobilePhone}
                onChange={(e) => setNewHelper({...newHelper, mobilePhone: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Email Address"
                value={newHelper.email}
                onChange={(e) => setNewHelper({...newHelper, email: e.target.value})}
                required
              />
              <div className="image-upload-section">
                <label htmlFor="image-upload">Profile Image</label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                {newHelper.image && (
                  <div className="image-preview">
                    <img src={newHelper.image} alt="Preview" style={{width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px'}} />
                  </div>
                )}
              </div>
              <button type="submit" className="button primary-button">Add Helper</button>
            </form>
          </div>
        )}

        <div className="helpers-grid">
          {helpers.map(helper => (
            <div key={helper.id} className="helper-card">
              <img src={helper.image} alt={helper.name} />
              <div className="helper-info">
                <h3>{helper.name}</h3>
                <p className="location">{helper.location}</p>
                <div className="specialties">
                  {helper.specialties.map((specialty, index) => (
                    <span key={index} className="specialty-tag">{specialty}</span>
                  ))}
                </div>
                {helper.herds && (
                  <div className="herds">
                    <small>Manages: {helper.herds.join(', ')}</small>
                  </div>
                )}
                {helper.capabilities && (
                  <div className="capabilities">
                    <small><strong>Capabilities:</strong> {helper.capabilities.join(', ')}</small>
                  </div>
                )}
                <div className="helper-stats">
                  <div className="rating">
                    <span className="stars">{renderStars(helper.rating)}</span>
                    <span className="rating-number">({helper.rating})</span>
                  </div>
                </div>
                <div className="helper-contact">
                  <p><i className="fas fa-phone"></i> {helper.homePhone}</p>
                  <p><i className="fas fa-mobile-alt"></i> {helper.mobilePhone}</p>
                  <p><i className="fas fa-envelope"></i> {helper.email}</p>
                </div>
                <div className="helper-actions">
                  <button
                    className="button secondary-button"
                    onClick={() => openRatingModal(helper.id)}
                  >
                    Rate Helper
                  </button>
                  <button
                    className="button danger-button"
                    onClick={() => handleRemoveHelper(helper.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showRatingModal && (
          <div className="rating-modal-overlay" onClick={() => setShowRatingModal(null)}>
            <div className="rating-modal" onClick={(e) => e.stopPropagation()}>
              <div className="rating-modal-header">
                <h3>Rate Helper</h3>
                <button 
                  className="close-button"
                  onClick={() => setShowRatingModal(null)}
                >
                  ×
                </button>
              </div>
              <div className="rating-modal-content">
                <p>How would you rate this helper's service?</p>
                <div className="star-rating">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      className={`star-button ${star <= (hoverRating || newRating) ? 'active' : ''}`}
                      onClick={() => setNewRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                    >
                      <i className="fas fa-star"></i>
                    </button>
                  ))}
                </div>
                <div className="rating-actions">
                  <button 
                    className="button secondary-button"
                    onClick={() => setShowRatingModal(null)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="button primary-button"
                    onClick={() => handleRateHelper(showRatingModal, newRating)}
                    disabled={newRating === 0}
                  >
                    Submit Rating
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Helpers;
